import {useProductCatalogue} from "~/contexts/productCatalogueContext";
import {getProductCatalogueDisplayName} from "~/utilities/productCatalogueDisplayName";
import type {ProductCatalogue} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/productCataloguesWorkspaceCapability/domain/ProductCatalogue";
import {useOptionalGeography} from "~/contexts/geographyContext";
import {useEffect, useRef, useState, type Dispatch, type ReactElement, type SetStateAction} from "react";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    UserDefinedObjectTemplateAdditionalColumnType as Type,
    UserDefinedObjectTemplateAdditionalColumnSourceType as Source,
    type UserDefinedObjectTemplateAdditionalColumn,
} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {UserDefinedObjectAdditionalColumnField} from "~/components/userDefinedObjectAdditionalColumnField";
import {
    formatUserDefinedObjectAdditionalColumnValue,
    userDefinedObjectAdditionalColumnDetailsMap,
    type UserDefinedObjectColumnValueContext,
} from "~/domainModels/userDefinedObjectAdditionalColumnValues";
import {useWorkspace, useWorkspaceUserTemplates, useOrganizationalUnitTemplates} from "~/contexts/workspaceContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {
    UserPicker,
    InlineUserPicker,
    OrganizationalUnitPicker,
    InlineOrganizationalUnitPicker,
    ProductPicker,
    InlineProductPicker,
} from "~/workspaceConfig/hostedForms/shared/entityPickers";
import {formatDateTime, getUserDisplayName, type PlatformUserDisplayDetails} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import {resolveGeographyAdditionalColumnValue} from "~/utilities/geographyValueResolver";

type DisplayArgs = {
    value: unknown;
    users: ReturnType<typeof useUsers>;
    userTemplates: ReturnType<typeof useWorkspaceUserTemplates>;
    organizationalUnits?: ReturnType<typeof useOrganizationalUnits>;
    organizationalUnitTemplates?: ReturnType<typeof useOrganizationalUnitTemplates>;
    products?: ReadonlyArray<ProductCatalogue>;
    platformUserDetailsByUserId?: ReadonlyMap<Uuid, PlatformUserDisplayDetails>;
    userDefinedObjectOptionsByTemplateId?: UserDefinedObjectColumnValueContext["referenceOptionsByTemplateId"];
};
type EditArgs = {
    value: unknown;
    setValue: Dispatch<SetStateAction<unknown>>;
    options?: ReadonlyArray<{id: Uuid; displayName: string}>;
    organizationalUnitId?: Uuid | null;
    productCatalogueTemplateId?: Uuid;
    allowedProductIds?: ReadonlySet<Uuid>;
    defaultValue?: {value: unknown; label: string};
};

/** Inline display fields commit on confirmation, so typing never issues object mutations. */
function InlineUdoField({
    column,
    value,
    setValue,
    options,
    context,
}: EditArgs & {column: UserDefinedObjectTemplateAdditionalColumn; context: UserDefinedObjectColumnValueContext}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    const [valid, setValid] = useState(true);
    const root = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        setDraft(value);
        setEditing(false);
    }, [column.id, value]);
    if (!editing) {
        return (
            <button
                type="button"
                onClick={() => setEditing(true)}
            >
                {formatUserDefinedObjectAdditionalColumnValue(column, value, context)}
            </button>
        );
    }
    return (
        <div ref={root}>
            <UserDefinedObjectAdditionalColumnField
                column={column}
                value={draft}
                options={options}
                context={context}
                onChange={setDraft}
                onValidityChange={setValid}
            />
            <button
                type="button"
                disabled={!valid}
                onClick={() => {
                    const input = root.current?.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("[data-udo-column-input]");
                    if (input !== null && input !== undefined && !input.reportValidity()) {
                        return;
                    }
                    setValue(draft);
                    setEditing(false);
                }}
            >
                Save
            </button>
            <button
                type="button"
                onClick={() => {
                    setDraft(value);
                    setEditing(false);
                }}
            >
                Cancel
            </button>
        </div>
    );
}

function WorkspaceUdoField({
    column,
    inline = false,
    readOnly = false,
    ...args
}: EditArgs & {column: UserDefinedObjectTemplateAdditionalColumn; inline?: boolean; readOnly?: boolean}): ReactElement {
    const {userDefinedObjectOptionsByTemplateId, platformUserDetails, productCatalogueTemplates} = useWorkspace();
    const products = useProductCatalogue();
    const geography = useOptionalGeography();
    const users = useUsers();
    const userTemplates = useWorkspaceUserTemplates();
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const context = {
        referenceOptionsByTemplateId: userDefinedObjectOptionsByTemplateId,
        resolveUser: (id: Uuid) => getUserDisplayName(id, users, userTemplates, new Map(platformUserDetails.map((user) => [user.userId, user]))),
        resolveUserTemplate: (id: Uuid) => userTemplates.find((template) => template.id === id)?.displayName ?? "?",
        resolveGeography: resolveGeographyAdditionalColumnValue,
        resolveOrganizationalUnit: (id: Uuid) => {
            const unit = organizationalUnits.find((item) => item.id === id);
            const template = organizationalUnitTemplates.find((item) => item.id === unit?.templateId);
            return unit !== undefined && template !== undefined
                ? processOttopilotStringTemplate(template.instanceDisplayNameTemplate, unit.additionalColumnValues)
                : "?";
        },
        resolveProduct: (id: Uuid) => {
            const product = products?.find((item) => item.id === id);
            return product === undefined ? "?" : getProductCatalogueDisplayName(product, productCatalogueTemplates);
        },
        formatTimestamp: formatDateTime,
    };
    if (readOnly || column.source.type === Source.resolver) {
        return (
            <UserDefinedObjectAdditionalColumnField
                column={column}
                value={args.value}
                context={context}
                readOnly
            />
        );
    }
    const id = typeof args.value === "string" ? (args.value as Uuid) : null;
    if (column.type === Type.user) {
        const Picker = inline ? InlineUserPicker : UserPicker;
        return (
            <Picker
                value={id}
                setValue={args.setValue}
                organizationalUnitId={args.organizationalUnitId}
            />
        );
    }
    if (column.type === Type.ou) {
        const Picker = inline ? InlineOrganizationalUnitPicker : OrganizationalUnitPicker;
        return (
            <Picker
                value={id}
                setValue={args.setValue}
            />
        );
    }
    if (column.type === Type.product) {
        const Picker = inline ? InlineProductPicker : ProductPicker;
        return (
            <Picker
                value={id}
                setValue={args.setValue}
                productCatalogueTemplateId={args.productCatalogueTemplateId}
                allowedProductIds={args.allowedProductIds}
            />
        );
    }
    const geographyOptions =
        column.type === Type.geographyCountries
            ? geography?.countries.map((item) => ({id: item.id, displayName: item.name}))
            : column.type === Type.geographyIndiaCities
              ? geography?.indiaCities.map((item) => ({id: item.id, displayName: item.name}))
              : column.type === Type.geographyIndiaPincodes
                ? geography?.indiaPincodes.map((item) => ({id: item.id, displayName: item.pincode}))
                : column.type === Type.geographyIndiaStates
                  ? geography?.indiaStates.map((item) => ({id: item.id, displayName: item.name}))
                  : column.type === Type.geographyIndiaZones
                    ? geography?.indiaZones.map((item) => ({id: item.id, displayName: item.name}))
                    : undefined;
    const options =
        args.options ??
        geographyOptions ??
        (column.type === Type.userTemplate
            ? userTemplates.map((template) => ({id: template.id, displayName: template.displayName}))
            : column.type === Type.ous
              ? organizationalUnits
                    .filter((unit) => unit.templateId === column.configuration.ouTemplateId)
                    .map((unit) => ({
                        id: unit.id,
                        displayName: processOttopilotStringTemplate(
                            organizationalUnitTemplates.find((template) => template.id === unit.templateId)?.instanceDisplayNameTemplate ?? "",
                            unit.additionalColumnValues,
                        ),
                    }))
              : undefined);
    if (inline) {
        return (
            <InlineUdoField
                {...args}
                column={column}
                options={options}
                context={context}
            />
        );
    }
    return (
        <UserDefinedObjectAdditionalColumnField
            column={column}
            value={args.value}
            context={context}
            options={options}
            onChange={args.setValue}
        />
    );
}

/** One UI implementation for core columns, bound to the actual column rather than a capability type enum. */
function buildUserDefinedObjectAdditionalColumnDetails(column: UserDefinedObjectTemplateAdditionalColumn) {
    const descriptor = userDefinedObjectAdditionalColumnDetailsMap[column.type];
    return {
        displayName: descriptor.displayName,
        defaultValueGenerator: (_args?: unknown) => descriptor.defaultValue(),
        randomValueGenerator: (_args?: unknown) => descriptor.defaultValue(),
        stringValueGetter: (args: DisplayArgs): string =>
            formatUserDefinedObjectAdditionalColumnValue(column, args.value, {
                formatTimestamp: formatDateTime,
                referenceOptionsByTemplateId: args.userDefinedObjectOptionsByTemplateId,
                resolveUser: (id) => getUserDisplayName(id, args.users, args.userTemplates, args.platformUserDetailsByUserId),
                resolveUserTemplate: (id) => args.userTemplates.find((template) => template.id === id)?.displayName ?? "?",
                resolveOrganizationalUnit: (id) => {
                    const unit = args.organizationalUnits?.find((item) => item.id === id);
                    const template = args.organizationalUnitTemplates?.find((item) => item.id === unit?.templateId);
                    return unit !== undefined && template !== undefined
                        ? processOttopilotStringTemplate(template.instanceDisplayNameTemplate, unit.additionalColumnValues)
                        : "?";
                },
                resolveProduct: (id) => {
                    const product = args.products?.find((item) => item.id === id);
                    const color = product?.additionalColumnValues["ec16634a-156b-48eb-9d32-1364f0b80f42" as Uuid];
                    return typeof color === "string" && color.trim() !== "" ? color : (product?.id ?? "?");
                },
                resolveGeography: resolveGeographyAdditionalColumnValue,
            }),
        displayComponent: ({value}: {value: unknown}) => (
            <WorkspaceUdoField
                column={column}
                value={value}
                setValue={() => {}}
                readOnly
            />
        ),
        editComponent: (args: EditArgs) => (
            <WorkspaceUdoField
                {...args}
                column={column}
            />
        ),
        inlineEditComponent: (args: EditArgs) => (
            <WorkspaceUdoField
                {...args}
                column={column}
                inline
            />
        ),
    };
}

const detailsByColumn = new WeakMap<UserDefinedObjectTemplateAdditionalColumn, ReturnType<typeof buildUserDefinedObjectAdditionalColumnDetails>>();

/** Keep editor component identities stable while a metadata snapshot is unchanged, preserving focus and drafts. */
export function getUserDefinedObjectAdditionalColumnDetails(column: UserDefinedObjectTemplateAdditionalColumn) {
    const existing = detailsByColumn.get(column);
    if (existing !== undefined) {
        return existing;
    }
    const details = buildUserDefinedObjectAdditionalColumnDetails(column);
    detailsByColumn.set(column, details);
    return details;
}
